import { Alert, Box, Tab } from "@mui/material";
import { Field, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import AppButton from "../../../../AppComponents/AppButton";
import { LoaderComponentProps, withLoader } from "../../../../AppComponents/Loader";
import AppInput from "../../../../AppComponents/ui/app-input";
import { Label } from "../../../../Customization/CommonElements";
import { useNotification } from "../../../../Hooks/useNotification/useNotification";
import { useAppDispatch } from "../../../../Store";
import { AppActions } from "../../../../Store/Slices/AppStore/appStore.action";
import {
  AppDetailTabs,
  AppStoreWrapper,
} from "../../AppDetail/AppDetailStyles";
import NavigationBar from "../../NavigationBar";
import { CoreService } from "../../../../Services/Apis/CoreApis";
import { setConfigData } from "../../../../Store/Slices/AuthSlice";
interface Props extends LoaderComponentProps {}
// Validation schema
const validationSchema = Yup.object().shape({
  store_url: Yup.string()
    .matches(
      /^(?!:\/\/)([a-zA-Z0-9-_]+\.)?[a-zA-Z0-9-_]+\.[a-zA-Z]{2,11}?$/i,
      "Invalid URL"
    )
    .required("Shopify store URL is required"),
  access_token: Yup.string()
    .required("Shopify Access Token is required"),
  api_key: Yup.string()
    .required("Shopify API Key is required"),
  api_secret: Yup.string()
    .required("Shopify API Secret is required"),
});
const ShopifyConfig: React.FC<Props> = ({ setLoading }) => {
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [userId, setUserId] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { showAlert } = useNotification();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Handle tab change
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };
  
  const navigatetoBack = () => {
    navigate(-1);
  };
  // Handle form submission
  const handleSubmit = (payload: any) => {
    // console.log("payload", payload);
    setLoading(true);
    dispatch(AppActions.connectShopifyAppManually(payload))
      .then((res: any) => {
        // console.log("res", res);
        if (res?.payload?.message) {
          showAlert("Shopify store connected successfully", "success");
          navigatetoBack();
        } else {
          showAlert(res?.payload?.error, "error");
        }
      })
      .catch((err) => {
        showAlert("Something went wrong", "error");
      })
      .finally (() => {
        setLoading(false);
        CoreService.tokenVerify()
          .then((res: any) => {
            if (res.data) {
              if (res.data.status) {
                dispatch(setConfigData({ payload: res?.data?.token }));
              }
            }
          })
          .catch((err: any) => {
            
          });
      });
  };
  useEffect(() => {
    setLoading(false)
  }, [])
  // Extract query parameters from the URL
  // Determine the message and severity for the Alert
  let alertMessage = "";
  let alertSeverity: "success" | "error" = "success";
  if (success == "1") {
    alertMessage = `Success! Your store ${userId} is successfully integrated with Geta.ai`;
    alertSeverity = "success";
  } else if (success == "0") {
    alertMessage = "Failed to complete the integration. Please try again.";
    alertSeverity = "error";
  }
  return (
    <AppStoreWrapper>
      <NavigationBar list={["App Store", "Shopify", "Configurations"]} />
      <Box className="body">
        <Box className="app__info">
          <AppDetailTabs
            value={selectedTab}
            onChange={handleChange}
            aria-label="app tabs"
          >
            <Tab label="Settings" />
          </AppDetailTabs>
          {success === "0" || success === "1" ? (
            <Alert
              style={{
                width: "fit-content",
                padding: "5px",
                margin: "10px 0",
              }}
              severity={alertSeverity}
            >
              {alertMessage}
            </Alert>
          ) : (
            <Box sx={{ flexGrow: 1, height: "100%" }} className="all__apps">
              <h2>Default Auth Parameters</h2>
              <Formik
                initialValues={{ store_url: "", access_token: "", api_key: "", api_secret: "" }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ errors, touched }) => (
                  <Form>
                    <div className="form__inner__div">
                      <div className="form__inner__div__row">
                        <Label className="form__inner__div__row__label">
                          Please enter your Shopify store URL:
                          <sup style={{ color: "red" }}>*</sup>
                        </Label>
                        <Field
                          name="store_url"
                          as={AppInput}
                          placeholder="example.com"
                          error={touched.store_url && !!errors.store_url}
                          helperText={touched.store_url && errors.store_url}
                          className="form__inner__div__row__input"
                        />
                        {touched.store_url && !!errors.store_url && (
                          <Label style={{ color: "red" }}>{errors.store_url}.</Label>
                        )}
                        <Label className="form__inner__div__row__label__desc">
                          If your shopify store url is{" "}
                          <b>https://example.com/admin/</b> or{" "}
                          <b>https://example.com</b> please enter <b>example.com</b>
                        </Label>
                      </div>
                      <div className="form__inner__div__row">
                        <Label className="form__inner__div__row__label">
                          Please enter your Shopify Access Token:
                          <sup style={{ color: "red" }}>*</sup>
                        </Label>
                        <Field
                          name="access_token"
                          as={AppInput}
                          placeholder=""
                          error={touched.access_token && !!errors.access_token}
                          helperText={touched.access_token && errors.access_token}
                          className="form__inner__div__row__input"
                          
                        />
                        {touched.access_token && !!errors.access_token && (
                          <Label style={{ color: "red" }}>{errors.access_token}.</Label>
                        )}
                      </div>
                      <div className="form__inner__div__row">
                        <Label className="form__inner__div__row__label">
                          Please enter your Shopify API key:
                          <sup style={{ color: "red" }}>*</sup>
                        </Label>
                        <Field
                          name="api_key"
                          as={AppInput}
                          placeholder=""
                          error={touched.api_key && !!errors.api_key}
                          helperText={touched.api_key && errors.api_key}
                          className="form__inner__div__row__input"
                        />
                        {touched.api_key && !!errors.api_key && (
                          <Label style={{ color: "red" }}>{errors.api_key}.</Label>
                        )}
                      </div>
                      <div className="form__inner__div__row">
                        <Label className="form__inner__div__row__label">
                          Please enter your Shopify API Secret:
                          <sup style={{ color: "red" }}>*</sup>
                        </Label>
                        <Field
                          name="api_secret"
                          as={AppInput}
                          placeholder=""
                          error={touched.api_secret && !!errors.api_secret}
                          helperText={touched.api_secret && errors.api_secret}
                          className="form__inner__div__row__input"
                        />
                        {touched.api_secret && !!errors.api_secret && (
                          <Label style={{ color: "red" }}>{errors.api_secret}.</Label>
                        )}
                      </div>
                    </div>
                    {/* <Alert
                      style={{
                        width: "fit-content",
                        padding: "5px",
                        margin: "10px 0",
                      }}
                      severity="info"
                    >
                      For integration to work seamlessly, make sure you're an
                      admin in Wordpress.
                    </Alert> */}
                    <div style={{ display: "flex", justifyContent: "right" }}>
                      <AppButton type="submit" variant="primarydark">
                        Continue
                      </AppButton>
                    </div>
                  </Form>
                )}
              </Formik>
            </Box>
          )}
        </Box>
      </Box>
    </AppStoreWrapper>
  );
};
export default withLoader(ShopifyConfig);